import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import ImageViewer from './ImageViewer';
import '../styles/GroupMembersPage.css';
const GroupMembersPage = ({ group, user, onBack, onGroupUpdated, onRequestLeaveGroup }) => {
  const navigate = useNavigate();
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [memberPendingRemoval, setMemberPendingRemoval] = useState(null);
  const [imageViewer, setImageViewer] = useState({ isOpen: false, imageUrl: '' });
  
  // Group edit states
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState(null);
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);
  const [editError, setEditError] = useState('');
  const [isUpdatingPinPermission, setIsUpdatingPinPermission] = useState(false);

  useEffect(() => {
    if (showAddMembers) {
      fetchAllContacts();
    }
  }, [showAddMembers]);

  useEffect(() => {
    if (showEditGroup && group) {
      setEditGroupName(group.name || '');
      setEditGroupDescription(group.description || '');
      setEditAvatarPreview(group.avatar ? `${config.apiUrl}${group.avatar}` : null);
      setEditAvatarFile(null);
      setEditError('');
    }
  }, [showEditGroup, group]);

  const fetchAllContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.apiUrl}/api/contacts/${user.id}`);
      setAllContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setMessage('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMembers = async () => {
    if (selectedContacts.length === 0) {
      setMessage('Please select at least one contact to add');
      return;
    }

    try {
      setAddingMembers(true);
      setMessage('');
      
      const response = await axios.post(`${config.apiUrl}/api/groups/${group._id}/members`, {
        memberIds: selectedContacts,
        userId: user.id
      });

      setMessage('Members added successfully!');
      setShowAddMembers(false);
      setSelectedContacts([]);
      
      // Refresh the group data or notify parent component.
      if (window.location.reload) {
        window.location.reload();
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error adding members:', error);
      setMessage(error.response?.data?.error || 'Failed to add members');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setAddingMembers(false);
    }
  };

  const handleContactToggle = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const isContactInGroup = (contactId) => {
    return group.members.some(member => member.user._id === contactId);
  };

  const isContactSelected = (contactId) => {
    return selectedContacts.includes(contactId);
  };

  const getMemberRole = (memberId) => {
    if (group.creator._id === memberId) return 'Creator';
    if (group.admins.some(admin => admin._id === memberId)) return 'Admin';
    return 'Member';
  };

  const isCurrentUserAdmin = () => {
    const creatorId = group.creator?._id || group.creator;
    if (creatorId && creatorId.toString() === user.id) return true;
    const admins = Array.isArray(group.admins) ? group.admins : [];
    return admins.some(a => (a._id || a).toString() === user.id);
  };

  const requestMemberRemoval = (member) => {
    const memberId = member.user._id;
    const isSelf = memberId === user.id;

    if (isSelf) {
      if (onRequestLeaveGroup) {
        onRequestLeaveGroup();
      }
      return;
    }

    const creatorId = (group.creator?._id || group.creator).toString();
    if (memberId === creatorId && !isSelf) {
      setMessage('Cannot remove group creator');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    setMemberPendingRemoval(member);
    setShowRemoveConfirm(true);
  };

  const handleRemoveMember = async (memberId) => {
    if (!isCurrentUserAdmin() && memberId !== user.id) return;
    if (memberId === (group.creator?._id || group.creator).toString()) {
      setMessage('Cannot remove group creator');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    try {
      await axios.delete(`${config.apiUrl}/api/groups/${group._id}/members/${memberId}`, {
        data: { userId: user.id }
      });
      // Refresh group data
      const refreshed = await axios.get(`${config.apiUrl}/api/groups/${group._id}`);
      if (onGroupUpdated) onGroupUpdated(refreshed.data);
      setMessage('Member removed');
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      console.error('Error removing member:', error);
      setMessage(error.response?.data?.error || 'Failed to remove member');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleConfirmRemoval = async () => {
    if (!memberPendingRemoval) return;
    const memberId = memberPendingRemoval.user._id;
    setShowRemoveConfirm(false);
    setMemberPendingRemoval(null);
    await handleRemoveMember(memberId);
  };

  const handleCancelRemoval = () => {
    setShowRemoveConfirm(false);
    setMemberPendingRemoval(null);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Creator': return '#ffc107';
      case 'Admin': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Check if user can edit the group
  const canEditGroup = () => {
    if (!group || !user) return false;
    const creatorId = group.creator?._id || group.creator;
    if (creatorId && creatorId.toString() === user.id) return true;
    const admins = Array.isArray(group.admins) ? group.admins : [];
    return admins.some(a => (a._id || a).toString() === user.id);
  };

  // Handle avatar file change
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setEditError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setEditError('Image size should be less than 5MB');
        return;
      }
      
      setEditAvatarFile(file);
      setEditError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove avatar
  const removeEditAvatar = () => {
    setEditAvatarFile(null);
    setEditAvatarPreview(null);
  };

  // Handle group update
  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    
    if (!editGroupName.trim()) {
      setEditError('Group name is required');
      return;
    }

    setIsUpdatingGroup(true);
    setEditError('');

    try {
      // Debug logging
      //console.log('Updating group with user ID:', user.id, typeof user.id);
      //console.log('Group ID:', group._id);
      //console.log('User object:', user);
      
      const formData = new FormData();
      formData.append('name', editGroupName.trim());
      formData.append('description', editGroupDescription.trim());
      formData.append('userId', user.id);
      
      // Add avatar file if selected
      if (editAvatarFile) {
        formData.append('avatar', editAvatarFile);
      }

      const response = await axios.put(`${config.apiUrl}/api/groups/${group._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update the parent component with new group data
      if (onGroupUpdated) {
        onGroupUpdated(response.data);
      }

      setShowEditGroup(false);
      setMessage('Group updated successfully!');
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Error updating group:', error);
      setEditError(error.response?.data?.error || 'Failed to update group');
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  const handlePinPermissionChange = async (pinPermission) => {
    if (!group?._id || !user?.id) return;
    if ((group.pinPermission || 'admins') === pinPermission) return;

    setIsUpdatingPinPermission(true);
    try {
      const response = await axios.put(
        `${config.apiUrl}/api/groups/${group._id}/pin-permission`,
        { userId: user.id, pinPermission }
      );
      if (onGroupUpdated && response.data?.group) {
        onGroupUpdated(response.data.group);
      }
      setMessage('Pin permission updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating pin permission:', error);
      setMessage(error.response?.data?.error || 'Failed to update pin permission');
    } finally {
      setIsUpdatingPinPermission(false);
    }
  };

  if (!group) return null;

  return (
    <div className="group-members-page">
      {/* Header */}
      <div className="members-page-header">
        <button className="back-button" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        
        <div className="header-content">
          <div className="group-info">
            <div className="group-avatar notranslate">
              {group.avatar ? (
                <img
                  src={`${config.apiUrl}${group.avatar}`}
                  alt={group.name}
                  onClick={() => {
                    setImageViewer({
                      isOpen: true,
                      imageUrl: `${config.apiUrl}${group.avatar}`,
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                  title="Click to view full image"
                />
              ) : (
                <div className="group-avatar-placeholder">
                  {group.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="group-details">
              <h1 className="group-name notranslate">{group.name}</h1>
              {group.description && (
                <p className="group-description notranslate">{group.description}</p>
              )}
              <p className="group-meta">
                {group.members.length} member{group.members.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          {canEditGroup() && (
            <button 
              className="edit-group-button"
              onClick={() => setShowEditGroup(true)}
              title="Edit Group"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit Group
            </button>
          )}
        </div>
      </div>

      {/* Remove/Leave Confirmation Modal */}
      {showRemoveConfirm && memberPendingRemoval && (
        <div className="gm-confirmation-overlay" onClick={handleCancelRemoval}>
          <div className="gm-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="gm-confirmation-title">Confirm Removal</h3>
            <p className="gm-confirmation-message">
              {`Remove ${memberPendingRemoval.user.username} from the group?`}
            </p>
            <div className="gm-confirmation-actions">
              <button className="gm-cancel-button" onClick={handleCancelRemoval}>Cancel</button>
              <button className="gm-confirm-button" onClick={handleConfirmRemoval}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Messages */}
      {message && (
        <div className={`page-message ${message.includes('successfully') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Members List */}
      <div className="members-content">
        {canEditGroup() && (
          <div className="members-section pin-permission-section">
            <h2 className="section-title">Pin Permissions</h2>
            <p className="pin-permission-help">
              Choose who can pin and unpin messages in this group.
            </p>
            <div className="pin-permission-options">
              <label className="pin-permission-option">
                <input
                  type="radio"
                  name="pinPermission"
                  value="admins"
                  checked={(group.pinPermission || 'admins') === 'admins'}
                  disabled={isUpdatingPinPermission}
                  onChange={() => handlePinPermissionChange('admins')}
                />
                <span>
                  <strong>Admins only</strong>
                  <em>Only group admins can pin messages</em>
                </span>
              </label>
              <label className="pin-permission-option">
                <input
                  type="radio"
                  name="pinPermission"
                  value="all"
                  checked={group.pinPermission === 'all'}
                  disabled={isUpdatingPinPermission}
                  onChange={() => handlePinPermissionChange('all')}
                />
                <span>
                  <strong>All members</strong>
                  <em>Every group member can pin messages</em>
                </span>
              </label>
            </div>
          </div>
        )}
        <div className="members-section">
          <div className="members-header">
            <h2 className="section-title">Group Members</h2>
            {canEditGroup() && (
              <button 
                className="add-members-button"
                onClick={() => setShowAddMembers(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add Members
              </button>
            )}
          </div>
          
          <div className="members-list">
            {group.members.map(member => {
              const role = getMemberRole(member.user._id);
              const roleColor = getRoleColor(role);
              
              return (
                <div key={member.user._id} className="member-card">
                  <div className="member-avatar">
                    {member.user.profileImage ? (
                      <img 
                        src={`${config.apiUrl}${member.user.profileImage}`} 
                        alt={member.user.username}
                      />
                    ) : (
                      <div className="member-avatar-placeholder notranslate">
                        {member.user.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  
                  <div className="member-info">
                    <div className="member-name notranslate">{member.user.username}</div>
                    <div className="member-role" style={{ backgroundColor: roleColor }}>
                      {role}
                    </div>
                  </div>
                  
                  <div className="member-actions">
                    <button className="action-btn" title="View Profile">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  {isCurrentUserAdmin() && member.user._id !== (group.creator?._id || group.creator).toString() && (
                    <button 
                      className="action-btn danger" 
                      title="Remove Member"
                      onClick={() => requestMemberRemoval(member)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18" />
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>
                  )}
                  {member.user._id === user.id && (
                    <button 
                      className="action-btn" 
                      title="Leave Group"
                      onClick={() => requestMemberRemoval(member)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 17l-5-5 5-5" />
                        <path d="M19 12H5" />
                      </svg>
                    </button>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add Members Modal */}
      {showAddMembers && (
        <div className="add-members-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Members to <span className='notranslate'>{group.name}</span></h3>
              <button 
                className="close-button" 
                onClick={() => {
                  setShowAddMembers(false);
                  setSelectedContacts([]);
                  setContactSearchQuery('');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="add-members-search">
              <svg className="add-members-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                className="add-members-search-input"
                placeholder="Search contacts..."
                value={contactSearchQuery}
                onChange={(e) => setContactSearchQuery(e.target.value)}
                autoFocus
              />
              {contactSearchQuery && (
                <button
                  type="button"
                  className="add-members-search-clear"
                  onClick={() => setContactSearchQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              )}
            </div>

            {message && (
              <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}>
                {message}
              </div>
            )}

            <div className="modal-body">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading contacts...</p>
                </div>
              ) : (
                <div className="contacts-list">
                  {(() => {
                    const query = contactSearchQuery.trim().toLowerCase();
                    const filteredContacts = query
                      ? allContacts.filter(c =>
                          (c.username && c.username.toLowerCase().includes(query)) ||
                          (c.phoneNumber && c.phoneNumber.toLowerCase().includes(query)) ||
                          (c.email && c.email.toLowerCase().includes(query))
                        )
                      : allContacts;

                    if (allContacts.length === 0) {
                      return (
                        <div className="no-contacts">
                          <p>No contacts available</p>
                        </div>
                      );
                    }

                    if (filteredContacts.length === 0) {
                      return (
                        <div className="no-contacts">
                          <p>No contacts match "{contactSearchQuery}"</p>
                        </div>
                      );
                    }

                    return filteredContacts.map(contact => {
                      const isInGroup = isContactInGroup(contact.id);
                      const isSelected = isContactSelected(contact.id);
                      
                      return (
                        <div
                          key={contact.id}
                          className={`contact-item ${isInGroup ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                          onClick={() => !isInGroup && handleContactToggle(contact.id)}
                        >
                          <div className="contact-avatar">
                            {contact.profileImage ? (
                              <img 
                                src={`${config.apiUrl}${contact.profileImage}`} 
                                alt={contact.username}
                                className="contact-profile-image"
                              />
                            ) : (
                              <div className="contact-avatar-placeholder notranslate">
                                {contact.username.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="contact-info">
                            <div className="contact-name notranslate">{contact.username}</div>
                            {isInGroup && (
                              <div className="contact-status">Already in group</div>
                            )}
                          </div>
                          <div className="contact-checkbox">
                            {isInGroup ? (
                              <span className="disabled-icon">✓</span>
                            ) : isSelected ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 6L9 17l-5-5"/>
                              </svg>
                            ) : null}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="cancel-button"
                onClick={() => {
                  setShowAddMembers(false);
                  setSelectedContacts([]);
                  setContactSearchQuery('');
                }}
              >
                Cancel
              </button>
              <button 
                className="add-button"
                onClick={handleAddMembers}
                disabled={addingMembers || selectedContacts.length === 0}
              >
                {addingMembers ? 'Adding...' : `Add ${selectedContacts.length} Member${selectedContacts.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditGroup && (
        <div className="edit-group-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Group</h3>
              <button 
                className="close-button" 
                onClick={() => {
                  setShowEditGroup(false);
                  setEditError('');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdateGroup} className="edit-group-form">
              <div className="form-group">
                <label htmlFor="editGroupName">Group Name *</label>
                <input
                  type="text"
                  id="editGroupName"
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                  placeholder="Enter group name"
                  maxLength="50"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="editGroupAvatar">Group Avatar</label>
                <div className="avatar-upload-container">
                  <input
                    type="file"
                    id="editGroupAvatar"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="avatar-input"
                  />
                  <label htmlFor="editGroupAvatar" className="avatar-upload-label">
                    {editAvatarPreview ? (
                      <div className="avatar-preview">
                        <img src={editAvatarPreview} alt="Avatar preview" />
                        <button type="button" onClick={removeEditAvatar} className="remove-avatar">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="avatar-placeholder">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                        </svg>
                        <span>Choose Image</span>
                      </div>
                    )}
                  </label>
                </div>
                <small>Recommended size: 200x200 pixels, max 5MB</small>
              </div>

              <div className="form-group">
                <label htmlFor="editGroupDescription">Description</label>
                <textarea
                  id="editGroupDescription"
                  value={editGroupDescription}
                  onChange={(e) => setEditGroupDescription(e.target.value)}
                  placeholder="Enter group description"
                  maxLength="200"
                  rows="3"
                />
              </div>

              {editError && <div className="error-message">{editError}</div>}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowEditGroup(false);
                    setEditError('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="update-button" 
                  disabled={isUpdatingGroup}
                >
                  {isUpdatingGroup ? 'Updating...' : 'Update Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ImageViewer
        isOpen={imageViewer.isOpen}
        imageUrl={imageViewer.imageUrl}
        onClose={() => setImageViewer({ isOpen: false, imageUrl: '' })}
      />
    </div>
  );
};

export default GroupMembersPage;
